import { allowedMime } from "@api";
import type { IDocument } from "@bbj-types/document";
import type { ObjectId } from "@bbj-types/utils";
import { WebsocketChannels } from "@bbj-types/websockets/body";
import { useWebsocketHandler } from "@root/utils/websocket";
import { type ChangeEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { IUploadFileFieldProps } from "../index";
import type { IUploadFileFieldOptions } from "../types";
import { handleErrors, maxFilesMessageBuilder, uploadErrorMessageBuilder } from "./errors";
import { useUploadTimeouts } from "./timeouts";
import { handleUpload } from "./upload";

export const INPUT_FILE_ALLOWED_MIME = (allowedMime ?? []).join(",");

export const useUploadFileField = (
  selectableDocuments: IDocument[],
  onChange: IUploadFileFieldProps["onChange"],
  onLoading: IUploadFileFieldProps["onLoading"],
  options: IUploadFileFieldOptions
) => {
  const [documents, setDocuments] = useState<(IDocument & { error?: boolean })[]>([]);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const loading = loadingUpload || documents.some((document) => !document.uploaded && !document.error);

  const handleUploadFailure = (documentId: string) => {
    setDocuments((docs) => {
      const index = docs.findIndex((doc) => doc._id === documentId);
      if (index === -1) {
        return docs;
      }
      return docs.toSpliced(index, 1, { ...docs[index], error: true });
    });
  };

  const notifiedFailuresRef = useRef<Set<ObjectId>>(new Set());
  useEffect(() => {
    for (const document of documents) {
      if (document.error && !notifiedFailuresRef.current.has(document._id)) {
        notifiedFailuresRef.current.add(document._id);
        window.publish("feedback", {
          severity: "error",
          message: `Le document "${document.name}" n'a pas pu être importé.`,
        });
      }
    }
  }, [documents]);

  const { startUploadTimeout, clearUploadTimeout } = useUploadTimeouts(handleUploadFailure);

  const handleOnChange = (documents: (IDocument & { error?: boolean })[]) => {
    if (!onChange) {
      return;
    }
    const filteredDocuments = documents.reduce<IDocument[]>((acc, curr) => {
      if (curr.uploaded) {
        acc.push(curr);
      }
      return acc;
    }, []);
    onChange(filteredDocuments);
  };

  const handleInputFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files === null || files.length < 1) {
      return;
    }
    setLoadingUpload(true);
    try {
      const errors = handleErrors(documents.length, files, options);
      if (errors.length > 0) {
        window.publish("feedback", {
          severity: "error",
          message: errors[0],
        });
      } else {
        const [newDocuments, filesErrors] = await handleUpload(files);
        if (filesErrors.length > 0) {
          window.publish("feedback", {
            severity: "error",
            message: uploadErrorMessageBuilder(filesErrors),
          });
        }
        if (newDocuments.length > 0) {
          for (const doc of newDocuments) {
            startUploadTimeout(doc._id);
          }
          setDocuments((documents) => [...documents, ...newDocuments]);
        }
      }
    } catch (error) {
      console.error(error);
      window.publish("feedback", {
        severity: "error",
        message:
          "Une erreur est survenue lors du téléchargement de vos fichiers. Veuillez contacter un administrateur.",
      });
    }
    setLoadingUpload(false);
  };

  const handleSelectedDocuments = (selectedDocuments: Set<ObjectId>) => {
    setDocuments((documents) => {
      const documentsSet = new Set<ObjectId>(documents.map((doc) => doc._id));
      const docsToAdd: IDocument[] = [];
      const docsToRemove = new Set<ObjectId>();
      for (const selectableDoc of selectableDocuments) {
        if (documentsSet.has(selectableDoc._id) && !selectedDocuments.has(selectableDoc._id)) {
          docsToRemove.add(selectableDoc._id);
        } else if (!documentsSet.has(selectableDoc._id) && selectedDocuments.has(selectableDoc._id)) {
          docsToAdd.push(selectableDoc);
        }
      }
      if (options.maxFiles && documents.length - docsToRemove.size + docsToAdd.length > options.maxFiles) {
        window.publish("feedback", {
          severity: "error",
          message: maxFilesMessageBuilder(docsToAdd, options),
        });
        return documents;
      }
      const newDocuments = documents.filter((doc) => !docsToRemove.has(doc._id));
      newDocuments.push(...docsToAdd);
      handleOnChange(newDocuments);
      return newDocuments;
    });
  };

  const handleRemove = (documentId: ObjectId) => {
    clearUploadTimeout(documentId);
    setDocuments((documents) => {
      const index = documents.findIndex((doc) => doc._id === documentId);
      if (index === -1) {
        return documents;
      }
      const newDocuments = documents.toSpliced(index, 1);
      if (documents[index].uploaded) {
        handleOnChange(newDocuments);
      }
      return newDocuments;
    });
  };

  useWebsocketHandler(WebsocketChannels.DOCUMENT, "document", (body) => {
    const newDocument = body.message;
    clearUploadTimeout(newDocument._id);
    if (!newDocument.uploaded) {
      handleUploadFailure(newDocument._id);
      return;
    }
    setDocuments((documents) => {
      const index = documents.findIndex((document) => document._id === newDocument._id);
      if (index !== -1) {
        const newDocuments = documents.toSpliced(index, 1, newDocument);
        handleOnChange(newDocuments);
        return newDocuments;
      }
      return documents;
    });
  });

  const prevLoading = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useLayoutEffect(() => {
    if (onLoading && prevLoading.current !== loading) {
      prevLoading.current = loading;
      onLoading(loading);
    }
  }, [loading]);

  return { documents, loading: loadingUpload, handleRemove, handleInputFileChange, handleSelectedDocuments };
};
